import React, { useEffect, useRef, useState } from 'react'
import type { FileRejection } from 'react-dropzone'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'

import { Button, ModalBox, Toasts, Upload } from '@fourthwall/components'
import { TrashIcon } from '@fourthwall/icons'
import { getCroppedImage } from '@modules/Dashboard/Product/components/ImageUpload/GalleryImageUploader.utils'

import * as S from './ThankYouCardUploader.styled'
import { ThankYouCardUploaderEmpty } from './ThankYouCardUploaderEmpty/ThankYouCardUploaderEmpty'

interface ThankYouCardsUploaderProps {
  initialImage?: string
  onConfirm?: (blob: Blob) => void
  onClose?: () => void
  onRemove?: () => void
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result.toString())
      }
    }

    reader.onerror = () => {
      reject(reader.error)
    }

    reader.readAsDataURL(blob)
  })
}

const base64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data)
  const byteArrays: Array<Uint8Array> = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })

  return blob
}

export const ThankYouCardsUploader = ({
  initialImage,
  onClose,
  onConfirm,
  onRemove,
}: ThankYouCardsUploaderProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [cropModal, setCropModal] = useState(false)
  const [aspectRatio, setAspectRatio] = useState(1800 / 1200)

  const sourceImage = useRef<string | undefined>(initialImage)
  const croppedImage = useRef<string>()

  const [image, setImage] = useState(initialImage)

  const onCropComplete = async (croppedArea: Area, croppedAreaPixels: Area) => {
    if (!sourceImage.current) {
      throw new Error('No image selected')
    }

    const blob = await getCroppedImage(sourceImage.current, croppedAreaPixels, 'image/jpeg')

    if (blob) {
      croppedImage.current = await blobToBase64(blob)
    } else {
      Toasts.notify('Unable to crop image')
    }
  }

  const handleConfirm = async () => {
    if (croppedImage.current) {
      const blob = base64toBlob(croppedImage.current.split(',')[1], 'image/jpeg')

      setImage(croppedImage.current)
      setCropModal(false)

      onConfirm?.(blob)
      onClose?.()
    }
  }

  const handleCancel = async () => {
    handleRemoveImage()
    setCropModal(false)
    onClose?.()
  }

  const handleFileDrop = async (files: File[]) => {
    if (files[0]) {
      const base64 = await blobToBase64(files[0])

      const loadImg = new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.src = base64
        img.onload = () => {
          resolve(img)
        }
        img.onerror = (error) => {
          reject(error)
        }
      })

      const img = await loadImg

      const ratio = img.width / img.height

      if (ratio >= 1) {
        setAspectRatio(1800 / 1200)

        if (img.width < 1800 || img.height < 1200) {
          Toasts.notify('Image must be at least 1800px x 1200px')

          return
        }
      } else {
        setAspectRatio(1200 / 1800)

        if (img.width < 1200 || img.height < 1800) {
          Toasts.notify('Image must be at least 1200px x 1800px')

          return
        }
      }

      sourceImage.current = base64

      setCropModal(true)
    } else {
      Toasts.notify('No file selected', { type: 'error' })
    }
  }

  const handleFileDropRejected = (rejectedFiles: FileRejection[]) => {
    rejectedFiles.forEach((rejection) => {
      rejection.errors.forEach((error) => {
        if (error.code === 'file-too-large') {
          Toasts.notify('File is too large')
        }
      })
    })
  }

  const handleRemoveImage = () => {
    croppedImage.current = undefined

    setImage(undefined)

    onRemove?.()
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return (
    <>
      {!image ? (
        <S.Upload>
          <Upload
            accept={{
              'image/jpeg': ['.jpg', '.jpeg'],
              'image/png': ['.png'],
            }}
            size="large"
            multiple={false}
            onFileDrop={handleFileDrop}
            onFileDropRejected={handleFileDropRejected}
            content={ThankYouCardUploaderEmpty}
          />
        </S.Upload>
      ) : (
        <S.Preview>
          <S.RemoveButton>
            <Button
              appearance="secondary"
              size="small"
              label={<TrashIcon />}
              onClick={handleRemoveImage}
            />
          </S.RemoveButton>
          <S.Image src={image} alt="Example of custom inserts" />
        </S.Preview>
      )}
      {cropModal && (
        <ModalBox
          title="Upload new custom insert"
          size="large"
          onClose={onClose}
          buttons={[
            <Button label="Cancel" appearance="secondary" onClick={handleCancel} />,
            <Button label="Confirm" appearance="primary" onClick={handleConfirm} />,
          ]}
          buttonsDirection="column"
        >
          <S.CropperContainer>
            <Cropper
              image={sourceImage.current}
              crop={crop}
              aspect={aspectRatio}
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
            />
          </S.CropperContainer>
        </ModalBox>
      )}
    </>
  )
}
